import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useLocation, getRoutePath } from "@/lib/simple-router";
import { getReturnPage } from "@/lib/return-page";
import { useState, useEffect, useRef, type KeyboardEvent, type Ref, type RefObject } from "react";
import { ArrowLeft, Users, Bed, Bath, Wifi, Car, ShowerHead, Mountain, CheckCircle, Calendar, Utensils, Tv, Waves, TreePine, Flame, Thermometer, Snowflake, Wind, Coffee, Zap, Shield, Home, Gamepad2, Dumbbell, Phone, Camera, Music, Speaker, Shirt, Bike, MapPin, Baby, Dog, Accessibility, Ban, ChefHat, Microwave, Refrigerator, WashingMachine, Sofa, Fan, Building, Trees, Sun, Moon, Lightbulb, Volume2, Laptop, Monitor, Mail, ChevronDown, ChevronUp, FileText, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import VillaConfigModal from "@/components/villa-config-modal";
import TermsModal from "@/components/terms-modal";
import PropertyMapModal from "@/components/property-map-modal";

import BookedDirectlyStyleWidget from "@/components/bookeddirectly-style-widget";
import PhotoGrid from "@/components/photo-grid";
import PhotoTourModal from "@/components/photo-tour-modal";
import MobileBookingBar from "@/components/mobile-booking-bar";
import Footer from "@/components/footer";
import { categorizeAmenities } from "@/data/property-amenities";
import type { Property, BedConfiguration } from "@shared/schema";
import { useGuestReviews, type GuestReview } from "@/hooks/use-guest-reviews";
import ReviewCard from "@/components/review-card";
import ReviewPlatformRatings from "@/components/review-platform-ratings";
import { useReviewCarousel } from "@/components/use-review-carousel";

export default function PropertyPage() {
  const params = useParams();
  const [location] = useLocation();
  const returnPage = getReturnPage(window.history.state?.previousPage, getRoutePath(location));
  const propertyId = params?.id;
  
  // State for expandable sections
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [isPhotoTourOpen, setIsPhotoTourOpen] = useState(false);
  const [isVillaConfigOpen, setIsVillaConfigOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [termsScrollTarget, setTermsScrollTarget] = useState<string | undefined>(undefined);
  const [isPropertyMapOpen, setIsPropertyMapOpen] = useState(false);

  // Extract search parameters for pre-filling booking form
  const searchParams = new URLSearchParams(window.location.search);
  const initialCheckIn = searchParams.get('checkIn') || '';
  const initialCheckOut = searchParams.get('checkOut') || '';
  const initialGuests = searchParams.get('guests') || '2';
  
  // Debug logging
  console.log('Property page URL params:', {
    location: window.location.search,
    checkIn: initialCheckIn,
    checkOut: initialCheckOut,
    guests: initialGuests
  });

  // Scroll to top when page loads (instant, no animation)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [propertyId]);

  // Preload all amenity icons for instant display when "Show More" is clicked
  useEffect(() => {
    const ICONS_PATH = '/api/assets/icons/icons/PNG';
    const iconPaths = [
      'shared-hot-tub.png',
      'lake-view.png',
      'beach-access-beachfrontguests-can-enjoy-a-nearby-beach.png',
      'wi-fi.png',
      'tv.png',
      'kitchenspace-where-guests-can-cook-their-own-meals.png',
      'stove.png',
      'oven.png',
      'microwave.png',
      'toaster.png',
      'coffee-maker-nespresso.png',
      'refrigerator.png',
      'dishwasher.png',
      'dishes-and-cutlerybowls-chopsticks-plates-cups-etc_1757736759744.png',
      'barbecue-utensilsgrill-charcoal-bamboo-skewersiron-skewers-etc_1757737521472.png',
      'central-air-conditioning.png',
      'heating.png',
      'indoor-fireplace-gas.png',
      'free-washing-machine-in-building.png',
      'free-dryer-in-building.png',
      'hair-dryer.png',
      'iron.png',
      'private-patio-or-balcony.png',
      'outdoor-furniture.png',
      'shared-backyard-not-fully-fencedan-open-space-on-the-property-usually-covered-in-grass.png',
      'kayak.png',
      'lanai_gazebo.png',
      'smoke-alarm.png',
      'fire-extinguisher.png',
      'first-aid-kit.png',
      'keypadcheck-yourself-into-the-home-with-a-door-code.png',
      'self-check-in.png',
      'highchair.png',
      'cot.png',
      'games_chess.png',
      'dedicated-workspacein-a-room-with-a-door.png',
      'bed-linen.png',
      'essentialstowels-bed-sheets-soap-and-toilet-paper.png',
      'extra-pillows-and-blankets.png',
      'dining-table.png',
      'hangers.png',
      'clothing-storage.png',
      'shampoo.png',
      'body-soap.png',
      'hot-water.png',
      'hot-water-kettle.png',
      'black-out-blinds.png',
      'cleaning-products.png',
      'wine-glasses.png',
      'baking-sheet.png',
      'private-entranceseparate-street-or-building-entrance.png',
      'floatplane.png',
      'free-parking-on-premises.png',
      'map_icon.png',
      'salt-shaker.png',
      'wine-glasses.png',
    ];
    
    iconPaths.forEach(path => {
      const img = new Image();
      img.src = `${ICONS_PATH}/${path}`;
    });
  }, []);

  const { data: property, isLoading, error } = useQuery<Property>({
    queryKey: [`/api/properties/${propertyId}`],
    enabled: !!propertyId,
  });

  const { data: bedConfigurations = [] } = useQuery<BedConfiguration[]>({
    queryKey: [`/api/properties/${propertyId}/bed-configurations`],
    enabled: !!propertyId,
  });

  // Fetch hero photos for the property detail page grid
  const { data: heroPhotosData } = useQuery<{ heroPhotos: string[], count: number }>({
    queryKey: [`/api/properties/${propertyId}/hero-photos`],
    enabled: !!propertyId,
  });

  // Use hero photos only if all 5 are selected, otherwise always use category-ordered property images
  // This ensures the gallery never shows empty during loading or with partial selections
  const displayImages = (heroPhotosData?.heroPhotos?.length === 5) 
    ? heroPhotosData.heroPhotos 
    : (property?.images || []);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="h-96 bg-gray-200 rounded-lg mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-6"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                </div>
              </div>
              <div>
                <div className="h-64 bg-gray-200 rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen pt-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">❌</span>
              </div>
              <h3 className="text-xl font-semibold text-toka-text mb-2">
                Property Not Found
              </h3>
              <p className="text-toka-text mb-6">
                The property you're looking for doesn't exist or has been removed.
              </p>
              <Link href="/villas">
                <Button className="bg-toka-primary hover:bg-toka-primary/90">
                  View All Properties
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const ICONS_PATH = '/api/assets/icons/icons/PNG';
  
  const amenityIcons: { [key: string]: any } = {
    // Spa & Wellness
    'Hot Tub': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/shared-hot-tub.png`} alt="Hot Tub" className={className} />,
    'Spa': ShowerHead,
    'Jacuzzi': ShowerHead,
    'Sauna': Thermometer,
    'Steam Room': Wind,
    
    // Views & Location
    'Lake View': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/lake-view.png`} alt="Lake View" className={className} />,
    'Lake Rotorua Views': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/lake-view.png`} alt="Lake View" className={className} />,
    'Mountain View': Mountain,
    'Ocean View': Waves,
    'Beach': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/beach-access-beachfrontguests-can-enjoy-a-nearby-beach.png`} alt="Beach" className={className} />,
    'Beach Access': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/beach-access-beachfrontguests-can-enjoy-a-nearby-beach.png`} alt="Beach Access" className={className} />,
    'Private Beach': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/beach-access-beachfrontguests-can-enjoy-a-nearby-beach.png`} alt="Private Beach" className={className} />,
    '100m of Private Beach': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/beach-access-beachfrontguests-can-enjoy-a-nearby-beach.png`} alt="Private Beach" className={className} />,
    'Waterfront': Waves,
    'Garden View': TreePine,
    'City View': Building,
    
    // Internet & Technology
    'WiFi': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/wi-fi.png`} alt="WiFi" className={className} />,
    'High-speed WiFi': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/wi-fi.png`} alt="WiFi" className={className} />,
    'Free WiFi': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/wi-fi.png`} alt="WiFi" className={className} />,
    'Free wifi': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/wi-fi.png`} alt="Free wifi" className={className} />,
    'Internet': Wifi,
    'TV': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/tv.png`} alt="Television" className={className} />,
    'Cable TV': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/tv.png`} alt="Television" className={className} />,
    'Streaming': Tv,
    'Netflix': Tv,
    'Smart TV': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/tv.png`} alt="Television" className={className} />,
    'Television': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/tv.png`} alt="Television" className={className} />,
    'Sound System': Speaker,
    'Bluetooth Speaker': Speaker,
    'Music System': Music,
    
    // Kitchen & Dining
    'Kitchen': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/kitchenspace-where-guests-can-cook-their-own-meals.png`} alt="Kitchen" className={className} />,
    'Full Kitchen': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/kitchenspace-where-guests-can-cook-their-own-meals.png`} alt="Kitchen" className={className} />,
    'Kitchenette': Utensils,
    'Dining Area': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/dining-table.png`} alt="Dining Area" className={className} />,
    'Microwave': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/microwave.png`} alt="Microwave" className={className} />,
    'Refrigerator': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/refrigerator.png`} alt="Refrigerator" className={className} />,
    'Fridge': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/refrigerator.png`} alt="Refrigerator" className={className} />,
    'Dishwasher': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/dishwasher.png`} alt="Dishwasher" className={className} />,
    'Dishes & Utensils': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/dishes-and-cutlerybowls-chopsticks-plates-cups-etc_1757736759744.png`} alt="Dishes & Utensils" className={className} />,
    'Coffee Machine': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/coffee-maker-nespresso.png`} alt="Coffee Maker" className={className} />,
    'Coffee Maker': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/coffee-maker-nespresso.png`} alt="Coffee Maker" className={className} />,
    'Coffee maker': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/coffee-maker-nespresso.png`} alt="Coffee Maker" className={className} />,
    'Espresso Machine': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/coffee-maker-nespresso.png`} alt="Espresso Machine" className={className} />,
    'Toaster': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/toaster.png`} alt="Toaster" className={className} />,
    'Oven': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/oven.png`} alt="Oven" className={className} />,
    'Stove': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/stove.png`} alt="Stove" className={className} />,
    'Pantry Items': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/salt-shaker.png`} alt="Pantry Items" className={className} />,
    'Wine Glasses': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/wine-glasses.png`} alt="Wine Glasses" className={className} />,
    'BBQ': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/barbecue-utensilsgrill-charcoal-bamboo-skewersiron-skewers-etc_1757737521472.png`} alt="Outdoor BBQ" className={className} />,
    'Grill': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/barbecue-utensilsgrill-charcoal-bamboo-skewersiron-skewers-etc_1757737521472.png`} alt="Outdoor BBQ" className={className} />,
    'Outdoor Grill': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/barbecue-utensilsgrill-charcoal-bamboo-skewersiron-skewers-etc_1757737521472.png`} alt="Outdoor BBQ" className={className} />,
    'Barbecue': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/barbecue-utensilsgrill-charcoal-bamboo-skewersiron-skewers-etc_1757737521472.png`} alt="Outdoor BBQ" className={className} />,
    'Outdoor BBQ': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/barbecue-utensilsgrill-charcoal-bamboo-skewersiron-skewers-etc_1757737521472.png`} alt="Outdoor BBQ" className={className} />,
    'Outdoor Furniture': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/outdoor-furniture.png`} alt="Outdoor Furniture" className={className} />,
    'Private Patio or Deck': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/private-patio-or-balcony.png`} alt="Private Patio or Deck" className={className} />,
    
    // Parking & Transportation
    'Parking': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/free-parking-on-premises.png`} alt="Parking" className={className} />,
    'Free Parking': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/free-parking-on-premises.png`} alt="Free Parking" className={className} />,
    'Free Parking on Premises': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/free-parking-on-premises.png`} alt="Free Parking on Premises" className={className} />,
    'Free parking on premises': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/free-parking-on-premises.png`} alt="Free parking on premises" className={className} />,
    'Garage': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/free-parking-on-premises.png`} alt="Garage" className={className} />,
    'Covered Parking': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/free-parking-on-premises.png`} alt="Covered Parking" className={className} />,
    'Covered Parking & Storage': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/free-parking-on-premises.png`} alt="Covered Parking" className={className} />,
    'Private Parking': Car,
    'EV Charging': Zap,
    'Electric Car Charging': Zap,
    'Bike Storage': Bike,
    'Bicycle': Bike,
    
    // Comfort & Climate
    'Air Conditioning': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/central-air-conditioning.png`} alt="Air Conditioning" className={className} />,
    'AC': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/central-air-conditioning.png`} alt="Air Conditioning" className={className} />,
    'Heating': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/heating.png`} alt="Heating" className={className} />,
    'Heat Pump': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/heating.png`} alt="Heat Pump" className={className} />,
    'Fireplace': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/indoor-fireplace-gas.png`} alt="Fireplace" className={className} />,
    'Gas Fireplace': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/indoor-fireplace-gas.png`} alt="Gas Fireplace" className={className} />,
    'Fan': Fan,
    'Ceiling Fan': Fan,
    'Ventilation': Wind,
    
    // Laundry & Cleaning
    'Washing Machine': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/free-washing-machine-in-building.png`} alt="Washing Machine" className={className} />,
    'Washer': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/free-washing-machine-in-building.png`} alt="Washer" className={className} />,
    'Dryer': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/free-dryer-in-building.png`} alt="Clothes Dryer" className={className} />,
    'Clothes Dryer': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/free-dryer-in-building.png`} alt="Clothes Dryer" className={className} />,
    'Laundry': WashingMachine,
    'Hair Dryer': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/hair-dryer.png`} alt="Hair Dryer" className={className} />,
    'Hair dryer': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/hair-dryer.png`} alt="Hair Dryer" className={className} />,
    'Iron': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/iron.png`} alt="Iron" className={className} />,
    'Iron and Board': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/iron.png`} alt="Iron and Board" className={className} />,
    'Iron & Board': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/iron.png`} alt="Iron & Board" className={className} />,
    'Ironing Board': Shirt,
    'Vacuum': Home,
    'Cleaning Supplies': Home,
    
    // Outdoor & Recreation
    'Balcony': Home,
    'Patio': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/private-patio-or-balcony.png`} alt="Patio" className={className} />,
    'Deck / Patio': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/private-patio-or-balcony.png`} alt="Deck / Patio" className={className} />,
    'Terrace': Home,
    'Deck': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/private-patio-or-balcony.png`} alt="Deck" className={className} />,
    'Outdoor Seating': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/outdoor-furniture.png`} alt="Outdoor Seating" className={className} />,
    'Outdoor furniture': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/outdoor-furniture.png`} alt="Outdoor Furniture" className={className} />,
    'Garden': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/shared-backyard-not-fully-fencedan-open-space-on-the-property-usually-covered-in-grass.png`} alt="Garden" className={className} />,
    'Lawn / Garden': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/shared-backyard-not-fully-fencedan-open-space-on-the-property-usually-covered-in-grass.png`} alt="Lawn / Garden" className={className} />,
    'Lawn': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/shared-backyard-not-fully-fencedan-open-space-on-the-property-usually-covered-in-grass.png`} alt="Lawn" className={className} />,
    'Pool': Waves,
    'Swimming Pool': Waves,
    'Private Pool': Waves,
    'Shared Pool': Waves,
    'Kayak': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/kayak.png`} alt="Kayak" className={className} />,
    'Kayak / Canoe': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/kayak.png`} alt="Kayak / Canoe" className={className} />,
    'Lanai / Gazebo': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/lanai_gazebo.png`} alt="Lanai / Gazebo" className={className} />,
    'Lanai': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/lanai_gazebo.png`} alt="Lanai" className={className} />,
    'Gazebo': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/lanai_gazebo.png`} alt="Gazebo" className={className} />,
    'Tennis Court': Gamepad2,
    'Golf': ({ className }: { className?: string }) => <img src="/api/assets/golf_1764902288295.png" alt="Golf" className={className} />,
    'Gym': Dumbbell,
    'Fitness Center': Dumbbell,
    'Exercise Equipment': Dumbbell,
    
    // Safety & Security
    'Security': Shield,
    'Safe': Shield,
    'Smoke Detector': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/smoke-alarm.png`} alt="Smoke Detector" className={className} />,
    'Smoke detector': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/smoke-alarm.png`} alt="Smoke detector" className={className} />,
    'Smoke alarm': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/smoke-alarm.png`} alt="Smoke alarm" className={className} />,
    'Fire Extinguisher': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/fire-extinguisher.png`} alt="Fire Extinguisher" className={className} />,
    'Fire extinguisher': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/fire-extinguisher.png`} alt="Fire extinguisher" className={className} />,
    'First Aid Kit': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/first-aid-kit.png`} alt="First Aid Kit" className={className} />,
    'First aid kit': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/first-aid-kit.png`} alt="First aid kit" className={className} />,
    'Security Camera': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/security-camera.png`} alt="Security Camera" className={className} />,
    'Security Cameras (Exterior Only)': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/security-camera.png`} alt="Security Cameras (Exterior Only)" className={className} />,
    'Alarm System': Shield,
    'Keyless Entry': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/keypadcheck-yourself-into-the-home-with-a-door-code.png`} alt="Keyless Entry" className={className} />,
    'Self check-in': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/self-check-in.png`} alt="Self check-in" className={className} />,
    'Self Check-in': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/self-check-in.png`} alt="Self Check-in" className={className} />,
    'Door Code Entry': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/keypadcheck-yourself-into-the-home-with-a-door-code.png`} alt="Door Code Entry" className={className} />,
    'Private Entrance': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/private-entranceseparate-street-or-building-entrance.png`} alt="Private Entrance" className={className} />,
    'Digital Lock': Shield,
    
    // Accessibility & Family
    'Wheelchair Accessible': Accessibility,
    'Accessible': Accessibility,
    'Baby Friendly': Baby,
    'Child Safe': Baby,
    'High Chair': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/highchair.png`} alt="High Chair" className={className} />,
    'Highchair': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/highchair.png`} alt="Highchair" className={className} />,
    'Crib': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/cot.png`} alt="Crib" className={className} />,
    'Cot': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/cot.png`} alt="Cot" className={className} />,
    'Pet Friendly': Dog,
    'Pets Allowed': Dog,
    'No Pets': Ban,
    'No Smoking': Ban,
    'Smoking Allowed': Ban,
    
    // Entertainment & Activities
    'Games': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/games_chess.png`} alt="Games" className={className} />,
    'Board Games': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/games_chess.png`} alt="Board Games" className={className} />,
    'Ping Pong Table': ({ className }: { className?: string }) => <img src="/api/assets/table-tennis_1764902664996.png" alt="Ping Pong Table" className={className} />,
    'Table Tennis': ({ className }: { className?: string }) => <img src="/api/assets/table-tennis_1764902664996.png" alt="Table Tennis" className={className} />,
    'Books': Home,
    'Reading Area': Home,
    'Workspace': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/dedicated-workspacein-a-room-with-a-door.png`} alt="Workspace" className={className} />,
    'Dedicated Workspace': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/dedicated-workspacein-a-room-with-a-door.png`} alt="Dedicated Workspace" className={className} />,
    'Dedicated workspace': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/dedicated-workspacein-a-room-with-a-door.png`} alt="Dedicated workspace" className={className} />,
    'Office': Laptop,
    'Desk': Laptop,
    'Meeting Room': Building,
    
    // Bedding & Furniture
    'Bed Linen': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/bed-linen.png`} alt="Bed Linen" className={className} />,
    'Bed linen': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/bed-linen.png`} alt="Bed linen" className={className} />,
    'Towels': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/bed-linen.png`} alt="Towels Provided" className={className} />,
    'Towels Provided': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/bed-linen.png`} alt="Towels Provided" className={className} />,
    'Linens Provided': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/bed-linen.png`} alt="Linens Provided" className={className} />,
    'Extra Pillows': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/extra-pillows-and-blankets.png`} alt="Extra Pillows" className={className} />,
    'Extra pillows and blankets': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/extra-pillows-and-blankets.png`} alt="Extra pillows and blankets" className={className} />,
    'Extra Pillows & Blankets': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/extra-pillows-and-blankets.png`} alt="Extra Pillows & Blankets" className={className} />,
    'Sofa Bed': Sofa,
    'Comfortable Seating': Sofa,
    'Dining Table': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/dining-table.png`} alt="Dining Table" className={className} />,
    'Living Area': Sofa,
    'Living Room': Sofa,
    'Hangers': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/hangers.png`} alt="Hangers" className={className} />,
    'Clothing storage': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/clothing-storage.png`} alt="Clothing storage" className={className} />,
    'Clothing Storage': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/clothing-storage.png`} alt="Clothing Storage" className={className} />,
    
    // Bathroom amenities
    'Shampoo': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/body-soap.png`} alt="Shampoo" className={className} />,
    'Body soap': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/body-soap.png`} alt="Body soap" className={className} />,
    'Body Soap': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/body-soap.png`} alt="Body Soap" className={className} />,
    'Body Soap, Shampoo & Conditioner': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/body-soap.png`} alt="Body Soap, Shampoo & Conditioner" className={className} />,
    'Hot water': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/hot-water.png`} alt="Hot water" className={className} />,
    'Hot water kettle': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/hot-water-kettle.png`} alt="Hot water kettle" className={className} />,
    'Hot Water Kettle': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/hot-water-kettle.png`} alt="Hot Water Kettle" className={className} />,
    
    // Cedar Spa
    'Cedar Spa Pools': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/shared-hot-tub.png`} alt="Cedar Spa Pools" className={className} />,
    
    // Utilities & Services
    'Electricity': Zap,
    'Power': Zap,
    'Water': Waves,
    'Hot Water': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/hot-water.png`} alt="Hot Water" className={className} />,
    'Lighting': Lightbulb,
    'Natural Light': Sun,
    'Blackout Curtains': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/black-out-blinds.png`} alt="Blackout Curtains" className={className} />,
    'Blackout blinds': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/black-out-blinds.png`} alt="Blackout blinds" className={className} />,
    'Blackout Blinds': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/black-out-blinds.png`} alt="Blackout Blinds" className={className} />,
    'Black Out Curtains': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/black-out-blinds.png`} alt="Black Out Curtains" className={className} />,
    'Curtains': Home,
    'Blinds': Home,
    
    // Essentials
    'Essentials': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/essentialstowels-bed-sheets-soap-and-toilet-paper.png`} alt="Essentials" className={className} />,
    'Cleaning products': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/cleaning-products.png`} alt="Cleaning products" className={className} />,
    'Cleaning Products': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/cleaning-products.png`} alt="Cleaning Products" className={className} />,
    'Cleaned with disinfectant': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/cleaning-products.png`} alt="Cleaned with disinfectant" className={className} />,
    'Cleaned with Disinfectant': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/cleaning-products.png`} alt="Cleaned with Disinfectant" className={className} />,
    'Wine glasses': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/wine-glasses.png`} alt="Wine glasses" className={className} />,
    'Baking sheet': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/baking-sheet.png`} alt="Baking sheet" className={className} />,
    'Baking Sheet': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/baking-sheet.png`} alt="Baking Sheet" className={className} />,
    'Private entrance': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/private-entranceseparate-street-or-building-entrance.png`} alt="Private entrance" className={className} />,
    'Floatplane': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/floatplane.png`} alt="Floatplane" className={className} />,
    'Floatplane Transfers': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/floatplane.png`} alt="Floatplane Transfers" className={className} />,
    'Golf Balls & Clubs': ({ className }: { className?: string }) => <img src={`${ICONS_PATH}/golf.png`} alt="Golf Balls & Clubs" className={className} />,
  };

  return (
    <div className="min-h-screen pt-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8">
        {/* Back Button */}
        <Link href={returnPage.href} className="mb-6 inline-flex items-center text-toka-primary font-normal text-[14px]">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {returnPage.label}
        </Link>

        {/* Property Images Grid - uses hero photos if 5 are selected, otherwise regular images */}
        <PhotoGrid
          images={displayImages}
          propertyName={property.name}
          onShowAllPhotos={() => setIsPhotoTourOpen(true)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Property Details */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4 line-clamp-3">
                {property.name}
              </h1>
              <p className="text-base sm:text-lg text-toka-text mb-4 line-clamp-2">{property.address}</p>
              
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-toka-text mb-6">
                <div className="flex items-center whitespace-nowrap">
                  <Users className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-sm sm:text-base">{property.maxGuests} guests</span>
                </div>
                <div className="flex items-center whitespace-nowrap">
                  <Bed className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-sm sm:text-base">{property.bedrooms} bedroom{property.bedrooms !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center whitespace-nowrap">
                  <Bath className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-sm sm:text-base">{property.bathrooms} bathroom{property.bathrooms !== "1" ? 's' : ''}</span>
                </div>
              </div>

              
            </div>

            {/* Description */}
            <div className="mb-8">
              <div className="prose prose-gray max-w-none">
                <div className="text-toka-text leading-relaxed text-lg space-y-4">
                  {showFullDescription ? (
                    property.description ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: property.description.replace(/\n/g, '<br>') }}
                        className="text-[16px]" />
                    ) : (
                      <p className="text-[16px] text-foreground/70">No description available for this property.</p>
                    )
                  ) : (
                    property.description ? (
                      <p className="text-[16px]">
                        {property.description.length > 200 ? 
                          `${property.description.substring(0, 200)}...` : 
                          property.description
                        }
                      </p>
                    ) : (
                      <p className="text-[16px] text-foreground/70">No description available.</p>
                    )
                  )}
                </div>
                <button
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  className="text-toka-primary hover:text-toka-primary-dark font-medium mt-2 flex items-center"
                >
                  {showFullDescription ? (
                    <>
                      Read Less
                      <ChevronUp className="ml-1 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Read More
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Property Highlights */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">Property Highlights</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {property?.name?.includes('Above Par') ? (
                  // Above Par Property Highlights
                  <>
                    <div className="flex items-center bg-transparent">
                      <img src="/api/assets/golf_1764902288295.png" alt="Golf Course Views" className="mr-3 h-6 w-6" />
                      <div>
                        <h3 className="font-medium text-foreground">Golf Course Views</h3>
                        <p className="text-sm text-toka-text">Stunning view of Arikikapakapa Golf Course</p>
                      </div>
                    </div>
                    <div className="flex items-center bg-transparent">
                      <img src="/api/assets/icons/icons/PNG/shared-hot-tub.png" alt="Spa Pool" className="mr-3 h-6 w-6" />
                      <div>
                        <h3 className="font-medium text-foreground">Spa Pool</h3>
                        <p className="text-sm text-toka-text">Private spa pool</p>
                      </div>
                    </div>
                    <div className="flex items-center bg-transparent">
                      <MapPin className="mr-3 h-6 w-6 text-toka-primary" />
                      <div>
                        <h3 className="font-medium text-foreground">Central Town Location</h3>
                        <p className="text-sm text-toka-text">Very close key amenities and tourist attractions</p>
                      </div>
                    </div>
                    <div className="flex items-center bg-transparent">
                      <Wifi className="mr-3 h-6 w-6 text-toka-primary" />
                      <div>
                        <h3 className="font-medium text-foreground">High-Speed WiFi</h3>
                        <p className="text-sm text-toka-text">Fast fiber internet throughout</p>
                      </div>
                    </div>
                  </>
                ) : (
                  // Regular Toka Ridge Property Highlights
                  <>
                    <div className="flex items-center bg-transparent">
                      <img src="/api/assets/icons/icons/PNG/lake-view.png" alt="Lake Views" className="mr-3 h-6 w-6" />
                      <div>
                        <h3 className="font-medium text-foreground">Lake Rotorua Views</h3>
                        <p className="text-sm text-toka-text">Stunning panoramic lake and mountain vistas</p>
                      </div>
                    </div>
                    <div className="flex items-center bg-transparent">
                      <img src="/api/assets/icons/icons/PNG/shared-hot-tub.png" alt="Spa Pools" className="mr-3 h-6 w-6" />
                      <div>
                        <h3 className="font-medium text-foreground">Cedar Spa Pools</h3>
                        <p className="text-sm text-toka-text">2 Cedar Spa pools with views</p>
                      </div>
                    </div>
                    <div className="flex items-center bg-transparent">
                      <img src="/api/assets/icons/icons/PNG/beach-access-beachfrontguests-can-enjoy-a-nearby-beach.png" alt="Beach Access" className="mr-3 h-6 w-6" />
                      <div>
                        <h3 className="font-medium text-foreground">100m of Private Beach</h3>
                        <p className="text-sm text-toka-text">Exclusive beach with kayaks and cabana</p>
                      </div>
                    </div>
                    <div className="flex items-center bg-transparent">
                      <img src="/api/assets/icons/icons/PNG/free-parking-on-premises.png" alt="Parking" className="mr-3 h-6 w-6" />
                      <div>
                        <h3 className="font-medium text-foreground">Covered Parking</h3>
                        <p className="text-sm text-toka-text">Garage with power and extra gear storage</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Amenities */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">Amenities</h2>
              {property.amenities && property.amenities.length > 0 ? (
                <div>
                  {/* Use categorizeAmenities to normalize and group property amenities */}
                  {(() => {
                    const categories = categorizeAmenities(property.amenities, property.name);
                    
                    // When collapsed, show only first category with max 4 items
                    if (!showAllAmenities && categories.length > 0) {
                      const firstCategory = categories[0];
                      const displayAmenities = firstCategory.amenities.slice(0, 4);
                      
                      return (
                        <>
                          <div className="mb-6">
                            <h3 className="text-lg font-medium mb-3 text-foreground">{firstCategory.name}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {displayAmenities.map((amenity, index) => {
                                const IconComponent = amenityIcons[amenity] || CheckCircle;
                                return (
                                  <div key={index} className="flex items-center bg-transparent transition-colors">
                                    <IconComponent className="mr-3 h-5 w-5 text-toka-primary flex-shrink-0" />
                                    <span className="text-toka-text text-sm">{amenity}{(amenity === 'Cot' || amenity === 'Highchair') ? ' (available on request)' : ''}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <button
                            onClick={() => setShowAllAmenities(true)}
                            className="text-toka-primary hover:text-toka-primary-dark font-medium mt-4 flex items-center"
                          >
                            Show all Amenities
                            <ChevronDown className="ml-1 h-4 w-4" />
                          </button>
                        </>
                      );
                    }
                    
                    // When expanded, show all categories with all amenities
                    return (
                      <>
                        {categories.map((category, categoryIndex) => (
                          <div key={categoryIndex} className="mb-6">
                            <h3 className="text-lg font-medium mb-3 text-foreground">{category.name}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {category.amenities.map((amenity, index) => {
                                const IconComponent = amenityIcons[amenity] || CheckCircle;
                                return (
                                  <div key={index} className="flex items-center bg-transparent transition-colors">
                                    <IconComponent className="mr-3 h-5 w-5 text-toka-primary flex-shrink-0" />
                                    <span className="text-toka-text text-sm">{amenity}{(amenity === 'Cot' || amenity === 'Highchair') ? ' (available on request)' : ''}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        <button
                          onClick={() => setShowAllAmenities(false)}
                          className="text-toka-primary hover:text-toka-primary-dark font-medium mt-4 flex items-center"
                        >
                          Show Less
                          <ChevronUp className="ml-1 h-4 w-4" />
                        </button>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-8 text-foreground/70">
                  <CheckCircle className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                  <p>Amenities information is being updated. Please contact us for details.</p>
                </div>
              )}
            </div>

            {/* The Neighbourhood */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">The Neighbourhood</h2>
              <div className="bg-background rounded-lg overflow-hidden">
                {/* Google Maps embed */}
                <div className="relative h-80 md:h-96">
                  <iframe
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(property.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen={true}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Property Location Map"
                    className="w-full h-full"
                  ></iframe>
                  
                  {/* Overlay button for fullscreen */}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-4 right-4 bg-background p-2 rounded-lg shadow-lg hover:bg-background/90 transition-colors text-foreground"
                    title="Open in Google Maps"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  </a>
                </div>
                
                {/* Map description */}
                <div className="p-4 bg-background">
                  <p className="text-sm text-foreground">
                    {property?.name?.includes('Above Par') ? (
                      "Glenholme is a quiet, character-rich suburb located in the heart of Rotorua. Known for its central location, the area offers easy access to local dining, shops, thermal attractions and the iconic Arikikapakapa Geothermal Golf Course. With leafy streets, established homes and a relaxed residential feel, Glenholme strikes a perfect balance between convenience and tranquillity."
                    ) : (
                      "Located in peaceful Hamurana, this property offers stunning lake views and easy access to Rotorua's attractions. The area is known for its tranquil setting, with the property situated on Hamurana Road overlooking Lake Rotorua."
                    )}
                  </p>
                  <div className="mt-3 flex items-center text-sm text-toka-primary">
                    <MapPin className="h-4 w-4 mr-1" />
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline cursor-pointer"
                    >
                      {property.address}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Location & Nearby Attractions */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">Location & Nearby Attractions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-transparent">
                  <div className="flex items-center mb-4">
                    <MapPin className="mr-3 h-6 w-6 text-toka-primary" />
                    <h3 className="text-lg font-semibold text-foreground">Prime Location</h3>
                  </div>
                  {property?.name?.includes('Above Par') ? (
                    // Above Par property distances
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span>Lake Rotorua</span>
                        <span className="text-toka-primary font-medium">5 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Grocery Store</span>
                        <span className="text-toka-primary font-medium">5 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Rotorua City Center</span>
                        <span className="text-toka-primary font-medium">5 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Rotorua Airport</span>
                        <span className="text-toka-primary font-medium">5 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Whakarewarewa Forest</span>
                        <span className="text-toka-primary font-medium">5 mins</span>
                      </div>
                    </div>
                  ) : (
                    // Toka Ridge property distances
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span>Lake Rotorua</span>
                        <span className="text-toka-primary font-medium">2 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Boat Ramp</span>
                        <span className="text-toka-primary font-medium">2 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Grocery Store</span>
                        <span className="text-toka-primary font-medium">10 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Rotorua City Center</span>
                        <span className="text-toka-primary font-medium">10 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Rotorua Airport</span>
                        <span className="text-toka-primary font-medium">20 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Whakarewarewa Forest</span>
                        <span className="text-toka-primary font-medium">20 mins</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="bg-transparent">
                  <div className="flex items-center mb-4">
                    <Mountain className="mr-3 h-6 w-6 text-toka-primary" />
                    <h3 className="text-lg font-semibold text-foreground">Top Attractions</h3>
                  </div>
                  {property?.name?.includes('Above Par') ? (
                    // Above Par property attractions
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span>Float Plane</span>
                        <span className="text-toka-primary font-medium">5 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Arikikapakapa Golf Course</span>
                        <span className="text-toka-primary font-medium">5 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Skyline Rotorua</span>
                        <span className="text-toka-primary font-medium">5 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Te Puia Geothermal Park</span>
                        <span className="text-toka-primary font-medium">3 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Redwoods Forest</span>
                        <span className="text-toka-primary font-medium">5 mins</span>
                      </div>
                    </div>
                  ) : (
                    // Toka Ridge property attractions
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span>Float Plane</span>
                        <span className="text-toka-primary font-medium">5 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Hamurana Springs & Golf Course</span>
                        <span className="text-toka-primary font-medium">20 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Skyline Rotorua</span>
                        <span className="text-toka-primary font-medium">5 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Te Puia Geothermal Park</span>
                        <span className="text-toka-primary font-medium">20 mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Redwoods Forest</span>
                        <span className="text-toka-primary font-medium">20 mins</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-6 text-[16px] text-foreground">
                For info on all local attractions and food scene read our{' '}
                <a 
                  href="/api/assets/PDFs/PDFs/toka_food_guide_1754463201702.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-toka-primary hover:underline font-semibold"
                  style={{ textDecorationColor: '#AD6433' }}
                >
                  food and fun guide
                </a>.
              </p>
            </div>

            {/* Sleeping Arrangements - Airbnb Style */}
            {(() => {
              // Use bed configurations if available, otherwise fall back to property beds
              if (bedConfigurations && bedConfigurations.length > 0) {
                // Sort by sortOrder to maintain admin-defined ordering
                const sortedConfigs = [...bedConfigurations].sort((a, b) => a.sortOrder - b.sortOrder);
                
                return (
                  <div className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4 text-foreground">Where you'll sleep</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {sortedConfigs.map((config, index) => (
                        <div key={config.id || index} className="group">
                          <div className="aspect-[4/3] rounded-xl overflow-hidden bg-background mb-3">
                            {config.photoUrl ? (
                              <img 
                                src={config.photoUrl} 
                                alt={config.bedroomName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-foreground/50">
                                <Bed className="h-12 w-12" />
                              </div>
                            )}
                          </div>
                          <h3 className="font-semibold text-foreground">{config.bedroomName}</h3>
                          <p className="text-sm text-foreground">{config.bedType}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              // Fallback to original bed data display
              const getDefaultBeds = (name: string, bedrooms: number) => {
                if (name.includes('1bd') || bedrooms === 1) {
                  return [{ type: 'Super King bed (can be split into King Singles)', count: 1 }];
                }
                if (name.includes('2bd') || bedrooms === 2) {
                  return [{ type: 'Queen bed', count: 2 }];
                }
                if (name.includes('Above Par')) {
                  return [
                    { type: 'King bed', count: 2 },
                    { type: 'Queen bed', count: 2 }
                  ];
                }
                
                if (bedrooms >= 1) {
                  return [{ type: 'King bed', count: bedrooms }];
                }
                
                return [];
              };

              const bedsToShow = (property.beds && property.beds.length > 0) 
                ? property.beds 
                : getDefaultBeds(property.name || '', property.bedrooms || 1);

              return bedsToShow.length > 0 ? (
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">Sleeping Arrangements</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                    {bedsToShow.map((bed, index) => {
                      const beds = [];
                      for (let i = 0; i < bed.count; i++) {
                        beds.push(
                          <Card key={`${bed.type}-${i}`} className="min-h-[100px]">
                            <CardContent className="p-4 h-full flex flex-col justify-center">
                              <div className="flex flex-col items-center text-center">
                                <div className="mb-2">
                                  <Bed className="h-5 w-5 text-toka-primary" />
                                </div>
                                <h3 className="font-medium text-toka-text">{bed.type}</h3>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      }
                      return beds;
                    }).flat()}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Google Reviews Section */}
            <ReviewsSection />

            {/* House Rules */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">Guest Information</h2>
              <Card className="border-0 shadow-none">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold mb-3 text-toka-primary flex items-center">
                        <img src="/api/assets/icons/icons/PNG/self-check-in.png" alt="Self Check-in" className="mr-2 h-5 w-5" />
                        Check-in/out
                      </h3>
                      <ul className="space-y-2 text-sm text-foreground">
                        <li>• Check-in: 2:00 PM</li>
                        <li>• Check-out: 10:00 AM</li>
                        <li>• Self Check-in with digital lock code</li>
                        <li>• Early Check-in subject to availability</li>
                        <li>• We do not offer late Check-out</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3 text-toka-primary">During your stay</h3>
                      <ul className="space-y-2 text-sm text-foreground">
                        <li>• Maximum {property.maxGuests} guests</li>
                        <li>• No smoking inside the villa</li>
                        <li>• No pets</li>
                        <li>• Children welcome</li>
                        <li>• Free parking on premises</li>
                        <li>• Events by request only</li>
                        <li>• Quiet hours: 9 PM - 7 AM</li>
                        <li>
                          <button 
                            onClick={() => {
                              setTermsScrollTarget('house-rules-section');
                              setIsTermsOpen(true);
                            }}
                            className="transition-colors underline text-foreground"
                            style={{ 
                              textDecorationColor: '#ae6632'
                            }}
                          >
                            • See all House Rules
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-border">
                    <h3 className="font-semibold mb-3 text-toka-primary">Additional Information</h3>
                    <div className="space-y-3">
                      <button 
                        onClick={() => window.open('/api/assets/PDFs/PDFs/tr_house_manual_for_website_1780102439433.pdf', '_blank')}
                        className="flex items-center text-toka-primary hover:text-toka-primary/80 transition-colors"
                      >
                        <Home className="mr-2 h-4 w-4" />
                        House Manual
                      </button>
                      <button 
                        onClick={() => setIsVillaConfigOpen(true)}
                        className="flex items-center text-toka-primary hover:text-toka-primary/80 transition-colors"
                      >
                        <Building className="mr-2 h-4 w-4" />
                        Villa Configurations
                      </button>
                      <button 
                        onClick={() => setIsPropertyMapOpen(true)}
                        className="flex items-center text-toka-primary hover:text-toka-primary/80 transition-colors"
                      >
                        <img src={`${ICONS_PATH}/map_icon.png`} alt="Map" className="mr-2 h-4 w-4" />
                        Map of the property
                      </button>
                      <button 
                        onClick={() => {
                          setTermsScrollTarget('cancellation-policy-section');
                          setIsTermsOpen(true);
                        }}
                        className="flex items-center text-toka-primary hover:text-toka-primary/80 transition-colors"
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Cancellation Policy
                      </button>
                      <button 
                        onClick={() => {
                          setTermsScrollTarget(undefined);
                          setIsTermsOpen(true);
                        }}
                        className="flex items-center text-toka-primary hover:text-toka-primary/80 transition-colors"
                      >
                        <Shield className="mr-2 h-4 w-4" />
                        Terms and Conditions
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Booking Form */}
          <div className="lg:col-span-1">
            <div id="booking-widget" tabIndex={-1} className="sticky top-24 scroll-mt-24 outline-none">
              <BookedDirectlyStyleWidget
                propertyId={property.id}
                propertyName={property.name}
                initialCheckIn={initialCheckIn}
                initialCheckOut={initialCheckOut}
                initialGuests={initialGuests}
              />



              {/* Contact Info */}
              <Card className="mt-6">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4 text-foreground">Need Help?</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center">
                      <Mail className="mr-3 h-4 w-4 text-toka-primary" />
                      <div>
                        <p className="font-medium text-foreground">Email Support</p>
                        <a 
                          href="mailto:info@tokaridge.co.nz" 
                          className="hover:text-toka-primary transition-colors text-foreground"
                        >
                          info@tokaridge.co.nz
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Phone className="mr-3 h-4 w-4 text-toka-primary" />
                      <div>
                        <p className="font-medium text-foreground">Phone Support</p>
                        <a 
                          href="tel:+6473460090" 
                          className="hover:text-toka-primary transition-colors text-foreground"
                        >
                          (+64) 7 346 0090
                        </a>
                      </div>
                    </div>
                    
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      {/* Photo Tour Modal */}
      <PhotoTourModal
        isOpen={isPhotoTourOpen}
        onClose={() => setIsPhotoTourOpen(false)}
        images={property.images || []}
        propertyName={property.name}
        categories={(property as any).hasCategorizedPhotos && (property as any).photoCategorizations ? (() => {
          const isAbovePar = property.id === "30606";
          const categoryOrder = [
            "Living room", "Full kitchen", "Dining area", "Bedroom", "Full bathroom",
            "Office", "Backyard", isAbovePar ? "Golf course" : "Beach", "Patio", "Laundry area", "Garage", "Exterior", "Hot tub", "Additional photos"
          ];
          const categorizations = (property as any).photoCategorizations;
          return categoryOrder
            .filter(category => categorizations[category] && categorizations[category].length > 0)
            .map(name => ({
              name,
              images: categorizations[name].map((p: any) => p.imageUrl)
            }));
        })() : undefined}
      />
      {/* Villa Configurations Modal */}
      <VillaConfigModal
        isOpen={isVillaConfigOpen}
        onClose={() => setIsVillaConfigOpen(false)}
      />
      {/* Terms and Conditions Modal */}
      <TermsModal
        isOpen={isTermsOpen}
        onClose={() => {
          setIsTermsOpen(false);
          setTermsScrollTarget(undefined);
        }}
        scrollToSection={termsScrollTarget}
      />
      {/* Property Map Modal */}
      <PropertyMapModal
        isOpen={isPropertyMapOpen}
        onClose={() => setIsPropertyMapOpen(false)}
      />
      <Footer />
      {/* Narrow screens only: jump-to-booking bar */}
      <MobileBookingBar key={propertyId} targetId="booking-widget" />
    </div>
  );
}

function ReviewsSection() {
  const [isReviewsModalOpen, setIsReviewsModalOpen] = useState(false);
  const [selectedReviewIndex, setSelectedReviewIndex] = useState<number | null>(null);
  const [expandedReviews, setExpandedReviews] = useState<Record<number, boolean>>({});
  const modalTriggerRef = useRef<HTMLButtonElement | null>(null);
  const { data, error, isLoading, refetch } = useGuestReviews();
  const reviews = data?.reviews ?? [];
  const openReview = (index: number, trigger?: HTMLButtonElement) => {
    if (trigger) modalTriggerRef.current = trigger;
    setSelectedReviewIndex(index);
    setIsReviewsModalOpen(true);
  };
  const closeReviews = () => {
    setIsReviewsModalOpen(false);
    setSelectedReviewIndex(null);
  };

  return (
    <div className="mb-8">
      <h2 className="text-2xl font-semibold mb-4 text-foreground">Guest Reviews</h2>
      <div className="mb-5 flex justify-center">
        <ReviewPlatformRatings data={data} variant="property" />
      </div>
      {data?.aggregateSource?.kind === "google-screenshot" && <p className="mb-2 text-center text-xs text-white/55">Rating and review count verified 25 September 2026.</p>}
      {data?.source?.kind === "snapshot" && data.source.capturedAt && <p className="mb-4 text-center text-xs text-white/55">Selected Google reviews · saved {new Date(data.source.capturedAt).toLocaleDateString("en-NZ")}. See Google for the latest reviews.</p>}
      {error && data && <p role="status" className="mb-5 text-sm text-foreground/70">Google reviews could not be updated. Showing the last available {reviews.length ? "results" : "information"}. <button type="button" onClick={() => refetch()} className="text-[#AD6433] underline underline-offset-4 hover:text-[#8B5025]">Try again</button></p>}
      {reviews.length > 0 ? <>
        <PropertyReviewsCarousel reviews={reviews.slice(0, 6)} expandedReviews={expandedReviews} onToggle={(index) => setExpandedReviews(current => ({ ...current, [index]: !current[index] }))} onSelect={openReview} />
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
          {data?.writeReviewUrl && <a href={data.writeReviewUrl} target="_blank" rel="noopener noreferrer" className="inline-block text-toka-primary transition-colors hover:text-toka-primary/80 text-[16px]">Review us on Google</a>}
        </div>
      </> : isLoading ? <ReviewLoading /> : error ? <div className="py-5"><p className="text-foreground/70">Guest reviews are unavailable right now.</p><button type="button" onClick={() => refetch()} className="mt-3 text-xs uppercase tracking-[0.14em] text-[#AD6433] underline underline-offset-4">Try again</button></div> : <div className="py-8 text-center"><p className="text-foreground">Guest reviews will appear here soon.</p>{data?.writeReviewUrl && <a href={data.writeReviewUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm text-toka-primary transition-colors hover:text-toka-primary/80">Leave a review on Google</a>}</div>}
      <ReviewsModal
        isOpen={isReviewsModalOpen}
        onClose={closeReviews}
        reviews={reviews}
        selectedReviewIndex={selectedReviewIndex}
        onSelectedReviewChange={setSelectedReviewIndex}
        triggerRef={modalTriggerRef}
      />
    </div>
  );
}

function PropertyReviewsCarousel({ reviews, expandedReviews, onToggle, onSelect }: {
  reviews: GuestReview[];
  expandedReviews: Record<number, boolean>;
  onToggle: (index: number) => void;
  onSelect: (index: number, trigger: HTMLButtonElement) => void;
}) {
  const carousel = useReviewCarousel(Object.values(expandedReviews).some(Boolean));
  const renderSet = (clone: boolean, setRef?: Ref<HTMLDivElement>) => <div ref={setRef} aria-hidden={clone || undefined} className="flex gap-5 pr-5">
    {reviews.map((review, index) => <ReviewCard key={`${clone ? "clone" : "review"}-${review.id}`} review={review} index={index} clone={clone} variant="property" expanded={!!expandedReviews[index]} onToggle={() => onToggle(index)} onSelect={onSelect} cardRef={!clone && index === 0 ? carousel.firstCardRef : undefined} />)}
  </div>;
  return <div ref={carousel.viewportRef} className="relative overflow-hidden touch-pan-y" onPointerDown={carousel.onPointerDown} onPointerMove={carousel.onPointerMove} onPointerUp={carousel.onPointerUp} onPointerCancel={carousel.onPointerUp} onClickCapture={carousel.onClickCapture} onMouseEnter={carousel.onMouseEnter} onMouseLeave={carousel.onMouseLeave} onFocus={carousel.onFocus} onBlur={carousel.onBlur}>
    <div ref={carousel.trackRef} className="flex w-max will-change-transform">{renderSet(true)}{renderSet(false, carousel.setRef)}{renderSet(true)}</div>
  </div>;
}

function ReviewLoading() {
  return <div className="grid grid-cols-1 gap-x-8 gap-y-7 md:grid-cols-2" aria-label="Loading guest reviews">
    {[0, 1].map(item => <div key={item} className="animate-pulse py-1">
      <div className="h-4 w-24 bg-muted" /><div className="mt-5 h-[120px] bg-muted" /><div className="mt-4 flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-muted" /><div className="h-4 w-24 bg-muted" /></div>
    </div>)}
  </div>;
}

function ReviewsModal({ 
  isOpen, 
  onClose, 
  reviews, 
  selectedReviewIndex,
  onSelectedReviewChange,
  triggerRef,
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  reviews: GuestReview[];
  selectedReviewIndex?: number | null;
  onSelectedReviewChange: (index: number | null) => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
}) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const reviewRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [expandedReviews, setExpandedReviews] = useState<Record<number, boolean>>({});

  // Initialize refs array when reviews change
  useEffect(() => {
    reviewRefs.current = reviewRefs.current.slice(0, reviews.length);
  }, [reviews]);

  // Scroll to selected review when modal opens
  useEffect(() => {
    if (isOpen && selectedReviewIndex !== null && selectedReviewIndex !== undefined) {
      requestAnimationFrame(() => {
        reviewRefs.current[selectedReviewIndex]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    }
  }, [isOpen, selectedReviewIndex]);

  const currentIndex = selectedReviewIndex ?? 0;
  const selectOffset = (offset: number) => onSelectedReviewChange(Math.max(0, Math.min(reviews.length - 1, currentIndex + offset)));
  const onDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") onClose();
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.preventDefault();
      selectOffset(1);
    }
    if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.preventDefault();
      selectOffset(-1);
    }
  };

  return <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
    {isOpen && <DialogContent onKeyDown={onDialogKeyDown} onCloseAutoFocus={(event) => { event.preventDefault(); triggerRef.current?.focus(); }} className="max-h-[80vh] w-[calc(100%-2rem)] max-w-2xl gap-0 overflow-hidden border-0 bg-[#2f3031] p-0 text-white sm:rounded-lg">
        <div className="flex flex-wrap items-start gap-x-2 gap-y-1 px-5 pb-3 pt-5 pr-14 sm:px-6 sm:pt-6">
          <div>
            <DialogTitle className="text-xl font-semibold text-white">All Reviews</DialogTitle>
            <DialogDescription className="mt-1 text-xs text-white/65">Use left and right arrow keys to move between reviews.</DialogDescription>
          </div>
          {reviews.length > 1 && <div className="flex min-h-10 items-center gap-1 sm:ml-auto">
            <button type="button" onClick={() => selectOffset(-1)} disabled={currentIndex === 0} aria-label="Show previous review" className="px-2 text-xs uppercase tracking-[0.08em] text-[#e0ad7b] disabled:cursor-not-allowed disabled:opacity-35 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#AD6433]">Previous</button>
            <button type="button" onClick={() => selectOffset(1)} disabled={currentIndex === reviews.length - 1} aria-label="Show next review" className="px-2 text-xs uppercase tracking-[0.08em] text-[#e0ad7b] disabled:cursor-not-allowed disabled:opacity-35 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#AD6433]">Next</button>
          </div>}
        </div>
        <div ref={modalContentRef} className="max-h-[calc(80vh-116px)] overflow-y-auto px-6 pb-6">
          {reviews.length > 0 ? (
            <div className="space-y-8">
              {reviews.map((review, index) => (
                <div
                  key={review.id}
                  ref={(el) => reviewRefs.current[index] = el}
                  data-review-index={index}
                  aria-current={currentIndex === index || undefined}
                  className={`rounded-md px-3 py-2 transition-colors ${currentIndex === index ? "bg-white/5" : ""}`}
                >
                  <ReviewCard review={review} index={index} variant="static" expanded={!!expandedReviews[index]} onToggle={() => setExpandedReviews(current => ({ ...current, [index]: !current[index] }))} />
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <p className="text-white/70">No reviews available at this time.</p>
            </div>
          )}
        </div>
    </DialogContent>}
  </Dialog>;
}
